import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import API from '../utils/api';
import PriceTag from '../components/PriceTag';
import { PROGRAM, APTITUDE_PROGRAM } from './Dsa';
import { MICRO_COURSES } from './Microinternships';

const V1 = '#7F00FF', V2 = '#B163FF';

const ArrowLeftIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
  </svg>
);

const CircuitBg = () => (
  <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden', zIndex: 0, opacity: 0.07 }}>
    <svg viewBox="0 0 1400 800" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ width: '100%', height: '100%' }}>
      <g stroke={V2} strokeWidth="0.9" fill="none">
        <line x1="0" y1="160" x2="320" y2="160" /><line x1="0" y1="420" x2="210" y2="420" />
        <circle cx="320" cy="160" r="5" fill={V1} opacity="0.9" /><circle cx="210" cy="420" r="4" fill={V2} opacity="0.9" />
      </g>
    </svg>
  </div>
);

// One row per priceable card — DSA + Aptitude (from Dsa.jsx) and every
// microinternship (from Microinternships.jsx), keyed exactly as /card-pricing expects.
const CARDS = [
  { cardKey: PROGRAM.cardKey, label: PROGRAM.title, defaultPrice: PROGRAM.defaultPrice, group: 'DSA' },
  { cardKey: APTITUDE_PROGRAM.cardKey, label: APTITUDE_PROGRAM.title, defaultPrice: APTITUDE_PROGRAM.defaultPrice, group: 'Aptitude' },
  ...MICRO_COURSES.map(c => ({ cardKey: `micro-${c.id}`, label: c.title, defaultPrice: c.defaultPrice, group: 'Microinternships' })),
];

const inputStyle = {
  width: '100%', background: 'var(--bg2)', border: '1.5px solid var(--border)',
  borderRadius: 10, color: 'var(--text)', fontFamily: "'Satoshi','DM Sans',sans-serif",
  fontSize: 14, padding: '10px 12px', outline: 'none', transition: 'border-color 0.2s, box-shadow 0.2s',
};
const onFocus = e => { e.target.style.borderColor = `${V1}88`; e.target.style.boxShadow = `0 0 0 3px ${V1}22`; };
const onBlur  = e => { e.target.style.borderColor = 'var(--border)'; e.target.style.boxShadow = 'none'; };

const fieldLabel = label => (
  <label style={{ display: 'block', fontSize: 10.5, color: 'var(--muted)', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6 }}>{label}</label>
);

const AdminCardPricing = () => {
  const [pricing, setPricing] = useState({});
  const [rows, setRows] = useState({}); // cardKey -> { oldPrice, newPrice } (as strings, for controlled inputs)
  const [savingKey, setSavingKey] = useState(null);

  const fetchPricing = () => API.get('/card-pricing').then(r => setPricing(r.data)).catch(() => {});
  useEffect(() => { fetchPricing(); }, []);

  useEffect(() => {
    setRows(prev => {
      const seeded = { ...prev };
      CARDS.forEach(c => {
        if (seeded[c.cardKey]) return; // don't clobber what the admin is currently typing
        const override = pricing[c.cardKey];
        seeded[c.cardKey] = {
          oldPrice: override?.oldPrice != null ? String(override.oldPrice) : '',
          newPrice: String(override?.newPrice ?? c.defaultPrice),
        };
      });
      return seeded;
    });
  }, [pricing]);

  const handleFieldChange = (cardKey, field, value) =>
    setRows(prev => ({ ...prev, [cardKey]: { ...prev[cardKey], [field]: value } }));

  const handleSave = async (cardKey) => {
    const row = rows[cardKey];
    if (row.newPrice.trim() === '' || isNaN(Number(row.newPrice))) {
      toast.error('Enter a valid new price');
      return;
    }
    setSavingKey(cardKey);
    try {
      await API.put(`/card-pricing/${cardKey}`, {
        oldPrice: row.oldPrice.trim() === '' ? null : Number(row.oldPrice),
        newPrice: Number(row.newPrice),
      });
      toast.success('Price updated');
      fetchPricing();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally {
      setSavingKey(null);
    }
  };

  let lastGroup = null;

  return (
    <div style={{ background: 'var(--bg)', color: 'var(--text)', minHeight: '100vh' }}>

      {/* HEADER */}
      <section style={{ padding: '120px 0 60px', position: 'relative', overflow: 'hidden', background: 'var(--bg)' }}>
        <CircuitBg />
        <div style={{ position: 'absolute', bottom: 0, right: 0, width: 300, height: 300, borderRadius: '50%', background: `radial-gradient(circle, var(--hero-orb) 0%, transparent 70%)`, pointerEvents: 'none' }} />
        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <Link to="/admin" style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            color: 'var(--muted)', fontSize: 13.5, fontWeight: 600, marginBottom: 24, transition: 'color 0.2s',
          }}
            onMouseEnter={e => e.currentTarget.style.color = V2}
            onMouseLeave={e => e.currentTarget.style.color = 'var(--muted)'}
          >
            <ArrowLeftIcon /> Admin Dashboard
          </Link>
          <div>
            <div className="section-label">ADMIN PANEL</div>
            <h1 style={{ fontSize: 'clamp(26px,4vw,44px)', fontWeight: 800, letterSpacing: '-0.025em', fontFamily: "'Inter',sans-serif" }}>
              Edit Card <span style={{ background: `linear-gradient(135deg,${V1},${V2})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Price</span>
            </h1>
            <p style={{ color: 'var(--muted)', fontSize: 14, marginTop: 6 }}>
              Set an old price to show it struck through next to the new price. Leave old price blank to just show the new price.
            </p>
          </div>
        </div>
      </section>

      {/* MAIN */}
      <section style={{ padding: '0 0 100px', background: 'var(--bg2)', borderTop: '1px solid var(--border)' }}>
        <div className="container" style={{ paddingTop: 40, maxWidth: 880 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {CARDS.map(c => {
              const row = rows[c.cardKey] || { oldPrice: '', newPrice: String(c.defaultPrice) };
              const showGroupHeading = c.group !== lastGroup;
              lastGroup = c.group;
              return (
                <React.Fragment key={c.cardKey}>
                  {showGroupHeading && (
                    <div style={{ fontSize: 11.5, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--muted)', marginTop: c.group === 'DSA' ? 0 : 20 }}>
                      {c.group}
                    </div>
                  )}
                  <div className="cardprice-row" style={{
                    background: 'var(--card-solid)', border: '1px solid var(--border)', borderRadius: 16,
                    padding: '18px 20px', boxShadow: 'var(--shadow-card)',
                    display: 'grid', gridTemplateColumns: '1.6fr 1fr 1fr 1fr auto', gap: 16, alignItems: 'end',
                  }}>
                    <div>
                      {fieldLabel('Card')}
                      <div style={{ fontWeight: 700, fontSize: 14.5 }}>{c.label}</div>
                    </div>

                    <div>
                      {fieldLabel('Old Price (₹)')}
                      <input
                        type="number" min="0" placeholder="None"
                        value={row.oldPrice}
                        onChange={e => handleFieldChange(c.cardKey, 'oldPrice', e.target.value)}
                        style={inputStyle} onFocus={onFocus} onBlur={onBlur}
                      />
                    </div>

                    <div>
                      {fieldLabel('New Price (₹)')}
                      <input
                        type="number" min="0"
                        value={row.newPrice}
                        onChange={e => handleFieldChange(c.cardKey, 'newPrice', e.target.value)}
                        style={inputStyle} onFocus={onFocus} onBlur={onBlur}
                      />
                    </div>

                    <div>
                      {fieldLabel('Preview')}
                      <PriceTag
                        oldPrice={row.oldPrice.trim() === '' ? null : Number(row.oldPrice)}
                        newPrice={row.newPrice.trim() === '' ? 0 : Number(row.newPrice)}
                        className="cardprice-preview"
                        oldSize={13}
                      />
                    </div>

                    <button
                      onClick={() => handleSave(c.cardKey)}
                      disabled={savingKey === c.cardKey}
                      style={{
                        background: `linear-gradient(135deg,${V1},${V2})`, color: '#fff', border: 'none',
                        borderRadius: 10, padding: '10px 18px', fontSize: 13.5, fontWeight: 700,
                        cursor: savingKey === c.cardKey ? 'not-allowed' : 'pointer',
                        opacity: savingKey === c.cardKey ? 0.7 : 1, whiteSpace: 'nowrap',
                      }}
                    >
                      {savingKey === c.cardKey ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </section>

      <style>{`
        .cardprice-preview { font-family: 'Inter',sans-serif; font-size: 20px; font-weight: 800; }
        @media (max-width: 760px) {
          .cardprice-row { grid-template-columns: 1fr 1fr !important; }
          .cardprice-row > div:first-child { grid-column: 1 / -1; }
        }
      `}</style>
    </div>
  );
};

export default AdminCardPricing;
